import { getApiClientConfig, getCurrentOrganizationId } from './config';

export class ApiError extends Error {
  public readonly status: number;
  public readonly details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

let sessionRenewal: Promise<void> | null = null;

/**
 * A dashboard can receive many authentication failures at once when its
 * one-hour access token expires. They all wait for the same refresh instead of
 * racing Cognito and repeatedly replacing the session cookies.
 */
const renewSessionOnce = async (
  renewSession: (() => Promise<void>) | undefined,
): Promise<boolean> => {
  if (!renewSession) {
    return false;
  }

  if (!sessionRenewal) {
    sessionRenewal = renewSession().finally(() => {
      sessionRenewal = null;
    });
  }

  try {
    await sessionRenewal;
    return true;
  } catch {
    return false;
  }
};

const buildRequestInit = async (
  method: HttpMethod,
  body?: unknown,
): Promise<RequestInit> => {
  const { credentials, getHeaders } = getApiClientConfig();

  const headers = new Headers({ 'Content-Type': 'application/json' });

  const organizationId = getCurrentOrganizationId();
  if (organizationId) {
    headers.set('X-Organization-Id', organizationId);
  }

  if (getHeaders) {
    for (const [key, value] of Object.entries(await getHeaders())) {
      headers.set(key, value);
    }
  }

  return {
    method,
    headers,
    credentials,
    body: body === undefined ? undefined : JSON.stringify(body),
  };
};

type AuthenticationAction = 'renew_session' | 'sign_in_required';

const getAuthenticationAction = async (
  response: Response,
): Promise<AuthenticationAction | null> => {
  if (response.status !== 403) {
    return null;
  }

  const payload = (await response
    .clone()
    .json()
    .catch(() => ({}))) as {
    action?: unknown;
  };

  return payload.action === 'renew_session' ||
    payload.action === 'sign_in_required'
    ? payload.action
    : null;
};

/**
 * Authentication failures can be an ordinary 401 or a structured 403 emitted
 * by API Gateway after the request authorizer denies an expired cookie. The
 * latter is distinct from a real permission-denied 403.
 *
 * Sign-in and refresh answer 401 in normal operation, so they opt out.
 */
export const request = async <TResponse, TBody = undefined>(
  path: string,
  method: HttpMethod,
  body?: TBody,
  options?: { skipUnauthorizedHandler?: boolean },
): Promise<TResponse> => {
  const { baseUrl, renewSession, onUnauthorized } = getApiClientConfig();

  let response = await fetch(
    `${baseUrl}${path}`,
    await buildRequestInit(method, body),
  );

  const authenticationAction = await getAuthenticationAction(response);
  const shouldRenew =
    !options?.skipUnauthorizedHandler &&
    (response.status === 401 || authenticationAction === 'renew_session');
  let renewalFailed = false;

  if (shouldRenew) {
    const renewed = await renewSessionOnce(renewSession);
    if (renewed) {
      // Rebuild the request so refreshed cookies or native authorization
      // headers are applied to the replay.
      response = await fetch(
        `${baseUrl}${path}`,
        await buildRequestInit(method, body),
      );
    } else {
      renewalFailed = true;
    }
  }

  if (response.status === 204) {
    return {} as TResponse;
  }

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const signInRequired =
      !options?.skipUnauthorizedHandler &&
      (response.status === 401 ||
        authenticationAction === 'sign_in_required' ||
        renewalFailed);

    if (signInRequired) {
      onUnauthorized?.();
    }

    throw new ApiError(
      (payload as { message?: string }).message ?? 'Request failed',
      response.status,
      (payload as { details?: unknown }).details,
    );
  }

  return payload as TResponse;
};
